import React, { useCallback, useState } from 'react';
import styled from 'styled-components';
import { SebBuild } from '../../../@resources/anim/SebBuild';
import { SebComponent } from '../../../@resources/anim/SebCommon';
import { SebMeeting } from '../../../@resources/anim/SebMeeting';
import { CommandDefinition } from '../../../concepts/commands/CommandDefinition';
import { CommandDispatcher } from '../../../concepts/commands/CommandDispatcher';
import { Colors, Grid } from '../../../ui';
import { Badge } from '../../../ui/Badge';
import { DropButton } from '../../../ui/DropButton';
import { useResolve } from '../../Container';
import { Copy, DisableFrame, Flexer } from '../../dashboard/elements/LayoutElements';
import { EditorModeInfo } from '../EditorModeInfo';
import { useEditorSessionObserver } from '../EditorSession';
import { MyRoleInfo } from '../MyRoleInfo';
import { PopoutWidget } from '../workspace/widgets/WidgetInterface';
import { DesignMode } from './DesignMode';
import { switchToDesignMode } from './DesignModeCommands';
import { IsElevated } from './IsElevated';
import { MeetingMode } from './MeetingMode';
import { switchToMeetingMode } from './MeetingModeCommands';

const Notice = styled.div`
    margin-top: 1rem;
    padding: 1rem;
    background: ${Colors.blue100};
    border-radius: .125rem;
    border: solid 0.0625rem ${Colors.blue200};
`;

const SebBuildWithOffset = (props: { active?: boolean }) =>
    <div style={{ transform: 'translateY(.75rem)' }}>
        <SebBuild {...props} />
    </div>;

const Divide = styled.div`
    position: relative;
    margin: .5rem 1rem;
    height: .0625rem;
    background: ${Colors.border};
`;

const OptionDropButton = styled(DropButton)`
    white-space: initial;
`;

type OptionProps = { seb: SebComponent, label: string, desc: string, selected?: boolean, onSelect: () => void };
const Option = ({ seb: Seb, label, desc, selected, onSelect }: OptionProps) => {
    const [active, setActive] = useState(false);
    const onEnter = () => setActive(true);
    const onLeave = () => setActive(false);
    return <Flexer onMouseEnter={onEnter} onMouseLeave={onLeave} onClick={onSelect}>
        <OptionDropButton overflow="0" padding="1" active={selected}>
            <Flexer flex="row" gap="1" padding=".5">
                <Flexer shrink width="4">
                    <Seb active={active || selected} />
                </Flexer>
                <Flexer flex="column" gap=".5">
                    <Flexer flex="row" align="center" justify="space-between">
                        <Copy bold flush size="1.25">{label}</Copy>
                        {selected && <Badge color={Colors.primary}>ACTIVE</Badge>}
                    </Flexer>
                    <Copy flush size=".875">{desc}</Copy>
                </Flexer>
            </Flexer>
        </OptionDropButton>
    </Flexer>;
};

export const EditorModeMenuWidget = ({ widget }: { widget: PopoutWidget }) => {

    const dispatch = useResolve(CommandDispatcher);
    const mode = useEditorSessionObserver(EditorModeInfo);
    const { canManage } = useEditorSessionObserver(MyRoleInfo);
    const isHost = useEditorSessionObserver(IsElevated);

    const onClose = useCallback(() => widget.update({ open: false }), [widget]);

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const onSelect = useCallback((command: CommandDefinition) => {
        dispatch(command);
        onClose();
    }, [onClose]);

    return <Flexer padding="1" width="28">
        <DisableFrame disabled={!canManage || !isHost}>
            <Grid columns="1" rows="1fr auto 1fr" >
                <div id="design-mode">
                    <Option
                        seb={SebBuildWithOffset}
                        label="Design Mode"
                        desc="Use Design Mode to customize the board design in crazy and exciting ways."
                        selected={mode.id === DesignMode.id}
                        onSelect={() => onSelect(switchToDesignMode)}
                    />
                </div>
                <Divide />
                <Option
                    seb={SebMeeting}
                    label="Meeting Mode"
                    desc="Switch to Meeting Mode and run a meeting on the board with your team."
                    selected={mode.id === MeetingMode.id}
                    onSelect={() => onSelect(switchToMeetingMode)}
                />
            </Grid>
        </DisableFrame>
        {!canManage && <Notice>
            <Copy size=".875" color={Colors.text} flush>
                As a guest, you do not have permission to start meetings or switch modes.
            </Copy>
        </Notice>}
        {!!canManage && !isHost && <Notice>
            <Copy size=".875" color={Colors.text} flush>
                Mode switching is disabled for participants during meetings.  Only hosts can switch mode.
            </Copy>
        </Notice>}
    </Flexer>;
};
